/**
 * AI Artifact Dashboard Module
 *
 * Displays AI-generated artifacts (plots, tables) from AI Teammate pinned workflows.
 * Supports optimistic rendering from TanStack Query cache during pin operations.
 */

import { useCallback, useContext, useMemo, useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { useAccountAlias } from "resources/account"
import { useResourceCurrency } from "resources/currencies"
import RestDataIntegrator from "gatherers/rest-data-integrator"
import { IDashboardContextValue } from "connected-components/dashboard/context/types"
import { useModules } from "../../context/modules"
import ArtifactRenderer from "renderers/ai-artifact"
import { ERROR, OK } from "helpers/data-statuses"
import type { DataStatus } from "helpers/data-statuses"
import type { ModuleRenderDependencies } from "../types"
import { createArtifactGatherer } from "./gatherer"
import type { AgentArguments, Artifact, PositionSet } from "./types"
import type { GridInitialState } from "@mui/x-data-grid-premium"
import { NotFoundError } from "api/errors"

import "./style.scss"

function unwrapSingleQueryResult<T>(data: T[]): T | null {
  return Array.isArray(data) && data.length > 0 ? data[0] : null
}

/**
 * AI Artifact Module Component
 *
 * Fetches and displays AI-generated artifacts using RestDataIntegrator
 * and delegates rendering to artifact templates based on kind.
 *
 * Supports optimistic rendering: if a pinnedId isn't available yet,
 * checks TanStack Query cache for temporary artifact data.
 */
export default function AiArtifactModule({
  TradeContext,
  moduleId,
}: Omit<ModuleRenderDependencies, "config"> & { moduleId: string }) {
  const queryClient = useQueryClient()
  const accountAlias = useAccountAlias()
  const { modules, updateModule, removeModule } = useModules()
  const dashboardContext = useContext(TradeContext) as IDashboardContextValue
  const { modelId, startDate, endDate, base, instrument } = dashboardContext
  const [currency] = useResourceCurrency(modelId, instrument)

  // Get the current module config from context instead of props
  // This ensures we always have the latest config, even when it updates
  const module = useMemo(
    () => modules.find(m => m.id === moduleId),
    [modules, moduleId],
  )
  const config = module?.config || {}
  const { pinnedId, kind, isPinning } = config
  const title = typeof config.title === "string" ? config.title.trim() : ""

  // Check TanStack Query cache for optimistic artifact (during pin operation)
  const optimisticArtifact = isPinning
    ? queryClient.getQueryData<Artifact>(["optimistic-artifact", moduleId])
    : null

  const agentArguments: AgentArguments = useMemo(() => {
    return {
      modelId,
      // Type assertion: In practice, instrument and base are always SelectedTypedInstrument (with id/type)
      // when used with AI artifacts, never SelectedInlineInstrument or SelectedRulesInstrument
      positionSet: instrument as PositionSet,
      base: base as PositionSet | undefined,
      startDate,
      endDate,
    }
  }, [modelId, instrument, base, startDate, endDate])

  // Create gatherer configuration when pinnedId is available
  const gatherer = useMemo(() => {
    if (!pinnedId) return null
    return createArtifactGatherer({
      pinnedId,
      agentArguments,
      accountAlias,
      kind,
    })
  }, [pinnedId, agentArguments, accountAlias, kind])

  // Memoize queries array to prevent unnecessary re-renders
  const queries = useMemo(() => {
    return gatherer ? [gatherer] : []
  }, [gatherer])

  const handleRetry = useCallback(() => {
    if (!gatherer) return
    queryClient.invalidateQueries({ queryKey: gatherer.key })
  }, [gatherer, queryClient])

  const onDataGridStateChange = useCallback(
    (dataGridState: GridInitialState) => {
      updateModule(moduleId, { config: { dataGridState } })
    },
    [moduleId, updateModule],
  )

  const rendererProps = useMemo(
    () => ({
      currency,
      title: title || undefined,
      dataGridState: config.dataGridState as GridInitialState | undefined,
      onDataGridStateChange,
      onRetry: handleRetry,
    }),
    [currency, title, config.dataGridState, onDataGridStateChange, handleRetry],
  )

  const onStatusChange = useCallback((status: DataStatus) => {
    if (status === ERROR && config.hasError !== true) {
      updateModule(moduleId, { config: { hasError: true } })
      return
    }

    if (status === OK && config.hasError) {
      updateModule(moduleId, { config: { hasError: false } })
    }
  }, [config.hasError, moduleId, updateModule])

  const handleQueryError = useCallback((error: unknown) => {
    if (error instanceof NotFoundError) {
      removeModule(moduleId)
    }
  }, [moduleId, removeModule])

  // Cleanup cache when pinning completes
  useEffect(() => {
    if (!isPinning && moduleId) {
      queryClient.removeQueries({ queryKey: ["optimistic-artifact", moduleId] })
    }
  }, [isPinning, moduleId, queryClient])

  // Render optimistically from TanStack cache — artifact is immediately visible
  // while the pin operation completes silently in the background.
  if (optimisticArtifact) {
    return (
      <div className="ai-artifact-module">
        <ArtifactRenderer
          status={OK}
          data={optimisticArtifact}
          key={`optimistic-${moduleId}`}
          {...rendererProps}
        />
      </div>
    )
  }

  // Wait for backend to generate pinnedId and gatherer configuration
  // Note: gatherer is only null when !pinnedId, so checking both only provides type safety
  if (!pinnedId || !gatherer) {
    if (isPinning) {
      return <div className="ai-artifact-module__status">Preparing your artifact...</div>
    }
    return <div className="ai-artifact-module__error">We couldn&apos;t load this artifact. Please try removing and pinning it again.</div>
  }

  return (
    <div className="ai-artifact-module">
      <RestDataIntegrator<Artifact>
        queries={queries}
        Renderer={ArtifactRenderer}
        rendererProps={rendererProps}
        dataTransform={unwrapSingleQueryResult}
        onStatusChange={onStatusChange}
        onError={handleQueryError}
        key={`artifact-${moduleId}-${pinnedId}`}
      />
    </div>
  )
}
